import React, { useEffect, useMemo, useState } from "react";
import SuccessPopup from "../../../components/common/SuccessPopup";
import ErrorPopup from "../../../components/common/ErrorPopup";

import "./AttendanceConfig.css";
import { GrAddCircle } from "react-icons/gr";
import { CiEdit } from "react-icons/ci";
import { RiDeleteBinLine } from "react-icons/ri";
import { LuPencilLine } from "react-icons/lu";
import config from "../../../config";
import Dropdown from "../../../components/Dropdown";

const AttendanceConfig = ({ userDetails, selectedClient }) => {
  const [filters, setFilters] = useState({ client: "", clientKey: "", jobRole: "", band: "" });
  const [clients, setClients] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [bands, setBands] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [showOtModal, setShowOtModal] = useState(false);
  const [otValue, setOtValue] = useState("");
  const [successPopup, setSuccessPopup] = useState(null);
  const [errorPopup, setErrorPopup] = useState(null);


  // Load dropdowns from APIs
  useEffect(() => {
    const orgKey = (userDetails?.orgId || "6").toString();
    const token = localStorage.getItem("Authorization") || userDetails?.token || "";

    const fetchClients = async () => {
      try {
        const url = `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgKey}`;
        const res = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const data = await res.json();
        setClients(Array.isArray(data.body) ? data.body : data ? [data] : []);
      } catch (error) {
        setClients([]);
      }
    };

    const fetchJobRoles = async () => {
      try {
        const url = `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgKey}?configType=JobRole`;
        const res = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.data || data?.content || [];
        setJobRoles(list.filter((i) => i.isActive !== false));
      } catch {
        setJobRoles([]);
      }
    };

    const fetchBands = async () => {
      try {
        // Prefer base API if available; else fall back to provided dev URL
        const baseUrl = `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgKey}?configType=Band`;
        let res = await fetch(baseUrl, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (!res.ok) {
          // Fallback to dev endpoint
          res = await fetch(`${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgKey}?configType=Band`);
        }
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.data || data?.content || [];
        setBands(list.filter((i) => i.isActive !== false));
      } catch {
        setBands([]);
      }
    };

    const fetchShiftTypes = async () => {
      try {
        const url = `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgKey}?configType=ShiftType`;
        const res = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.data || data?.content || [];
        setShiftTypes(list.filter((i) => i.isActive !== false));
      } catch {
        setShiftTypes([]);
      }
    };

    fetchClients();
    fetchJobRoles();
    fetchBands();
    fetchShiftTypes();
  }, [userDetails?.orgId, userDetails?.token]);

  const [shifts, setShifts] = useState([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);

  // Section 2: Attendance Time Sheet List
  const [timesheets, setTimesheets] = useState([]);
  const [isLoadingTimesheets, setIsLoadingTimesheets] = useState(false);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [editingTimesheetId, setEditingTimesheetId] = useState(null);
  const [timesheetDraft, setTimesheetDraft] = useState({
    category: "",
    deduction: "",
    work: "",
  });

  // Fetch Timesheets from API
  const fetchAttendanceSheets = async () => {
  if (!userDetails?.orgId || !filters.clientKey) {
    setErrorPopup("Please select a client first");
    return;
  }

  setIsLoadingTimesheets(true);

  try {
    const token =
      localStorage.getItem("Authorization") || userDetails?.token || "";

    const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHEET_ENDPOINT}/view/${userDetails.orgId}/${filters.clientKey}/${filters.jobRole || 0}/${filters.band || 0}`;

    const res = await fetch(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (res.ok) {
      const data = await res.json();
      console.log("Attendance Sheet View API:", data.body);

      const list = Array.isArray(data) ? data : data?.body || [];

      const transformed = list.map((item) => ({
  id: item.id,
  category: item.attendanceCategory || "",
  deduction: item.payDeductionPercent ?? "",   // ✅ FIXED
  work: item.presenceTime || "",
}));


      setTimesheets(transformed);
    } else {
      setTimesheets([]);
      setErrorPopup("Failed to load attendance sheets");
    }
  } catch (error) {
    console.error(error);
    setErrorPopup("Error while fetching attendance sheets");
    setTimesheets([]);
  } finally {
    setIsLoadingTimesheets(false);
  }
};

  const fetchTimesheets = async () => {
    if (!userDetails?.orgId) return;
    
    setIsLoadingTimesheets(true);
    try {
      const token = localStorage.getItem("Authorization") || userDetails?.token || "";
      const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHIFT_ENDPOINT}/list/${userDetails.orgId}`;
      const res = await fetch(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log("Fetched all attendance data:", data);
        // Filter only timesheet entries (those with attendance_category field)
        const transformedTimesheets = Array.isArray(data) 
          ? data
              .filter(item => item.attendanceCategory || item.attendance_category || item.payDeductionPercentage || item.pay_deduction_percentage)
              .map(item => ({
                id: item.id,
                category: item.attendanceCategory || item.attendance_category || "",
                deduction: item.payDeductionPercentage || item.pay_deduction_percentage || "",
                work: item.presenceTime || item.presence_time || "",
              }))
          : [];
        setTimesheets(transformedTimesheets);
      } else {
        console.error("Failed to fetch timesheets:", res.status);
        setTimesheets([]);
      }
    } catch (error) {
      console.error("Error fetching timesheets:", error);
      setTimesheets([]);
    } finally {
      setIsLoadingTimesheets(false);
    }
  };

  // Fetch timesheets on component mount - REMOVED TO REDUCE INITIAL PAGE LOAD API CALLS
  // useEffect(() => {
  //   fetchTimesheets();
  // }, [userDetails?.orgId, userDetails?.token]);

  const openEditTimesheet = (timesheet) => {
    setTimesheetDraft({
      category: timesheet.category || "",
      deduction: timesheet.deduction || "",
      work: timesheet.work || "",
    });
    setEditingTimesheetId(timesheet.id);
    setShowTimesheetModal(true);
  };

  const deleteTimesheet = async (id) => {
    if (!window.confirm("Are you sure you want to delete this timesheet?")) {
      return;
    }

    try {
      const token = localStorage.getItem("Authorization") || userDetails?.token || "";
      const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHEET_ENDPOINT}/delete/${userDetails.orgId}/${filters.clientKey}/${id}`;
    
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (res.ok) {
        await fetchTimesheets(); // Refresh data from DB
        setSuccessPopup("Timesheet deleted successfully!");
      } else {
        const errorData = await res.json().catch(() => null);
        setErrorPopup(errorData?.message || "Failed to delete timesheet");
      }
    } catch (error) {
      console.error("Error deleting timesheet:", error);
      setErrorPopup("An error occurred while deleting the timesheet");
    }
  };

  const handleSaveTimesheet = async () => {
    // Validate client selection
    if (!filters.client) {
      setErrorPopup("Please select a client first");
      return;
    }

    try {
      const token = localStorage.getItem("Authorization") || userDetails?.token || "";
      const orgKey = parseInt(userDetails?.orgId || "6");
      
      const timesheetData = {
        orgKey: orgKey,
        clientKey: parseInt(filters.clientKey) || null,
        roleId: parseInt(filters.jobRole) || null,
        bandId: parseInt(filters.band) || null,
        attendanceCategory: timesheetDraft.category,
        payDeductionPercent: timesheetDraft.deduction,
        presenceTime: timesheetDraft.work,
      };  

      if (editingTimesheetId) {
        // Update existing timesheet
        const updateData = {
          ...timesheetData,
          id: editingTimesheetId,
        };

        const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHEET_ENDPOINT}/update`;
        const res = await fetch(url, {
          method: 'PUT',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(updateData),
        });

        if (res.ok) {
          // Update local state with new values
          setTimesheets(timesheets.map(t => t.id === editingTimesheetId ? {
            ...t,
            category: timesheetDraft.category,
            deduction: timesheetDraft.deduction,
            work: timesheetDraft.work,
          } : t));
          setSuccessPopup("Timesheet updated successfully!");
        } else {
          const errorData = await res.json().catch(() => null);
          setErrorPopup(errorData?.message || "Failed to update timesheet");
        }
 } else {
  // Add new timesheet
  const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHEET_ENDPOINT}/add`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(timesheetData),
  });

  if (res.ok) {
    const newTimesheet = await res.json();

    setTimesheets([
      ...timesheets,
      {
        id: newTimesheet.id || Date.now(),
        category: timesheetDraft.category || "",
        deduction: timesheetDraft.deduction || "",
        work: timesheetDraft.work || "",
      },
    ]);

    setSuccessPopup("Timesheet added successfully!");
  } else {
    const errorData = await res.json().catch(() => null);
    setErrorPopup(errorData?.message || "Failed to add timesheet");
  }
}


      setShowTimesheetModal(false);
      setEditingTimesheetId(null);
      setTimesheetDraft({ category: "", deduction: "", work: "" });
    } catch (error) {
      console.error("Error saving timesheet:", error);
      setErrorPopup("An error occurred while saving the timesheet");
    }
  };

  // Section 3: CSV Mapping rows
  const [csvRows] = useState([
    { id: 1, clientHeader: "Name", component: "", format: "", map: true },
    { id: 2, clientHeader: "Date", component: "", format: "", map: false },
    { id: 3, clientHeader: "Employee Id", component: "Employee.employeeId", format: "", map: false },
    { id: 4, clientHeader: "Department name", component: "", format: "", map: false },
    { id: 5, clientHeader: "Shift", component: "", format: "", map: false },
    { id: 6, clientHeader: "Shift hours", component: "", format: "", map: false },
  ]);

  // CSV file upload state
  const [csvFile, setCsvFile] = useState(null);

  // File select handler
  const fileInputRef = React.useRef();
  const handleCsvFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCsvFile(e.target.files[0]);
    }
  };
  // Trigger file input click
  const handleUploadIconClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Remove selected file
  const handleRemoveCsvFile = () => {
    setCsvFile(null);
  };

  const filteredShifts = useMemo(() => shifts, [shifts]);
  const [otThreshold, setOtThreshold] = useState("");
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [editingShiftId, setEditingShiftId] = useState(null);


  const fetchShiftsByFilter = async () => {
  setIsLoadingShifts(true);

  try {
    const token =
      localStorage.getItem("Authorization") || userDetails?.token || "";

    const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHIFT_ENDPOINT}/list/${userDetails.orgId}/${filters.clientKey}/${filters.jobRole || 0}/${filters.band || 0}`;

    const res = await fetch(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (res.ok) {
      const data = await res.json();

      const shiftData = Array.isArray(data)
        ? data.filter((i) => i.shiftName || i.shift_name)
        : [];

      setShifts(
        shiftData.map((item) => ({
          id: item.id,
          name: item.shiftName || item.shift_name,
          shiftTimeFrom: item.shiftTimeFrom,
          shiftTimeTo: item.shiftTimeTo,
          allowance: item.shiftAllowance || 0,
          buffer: item.bufferTime || "",
        }))
      );
    } else {
      setShifts([]);
    }
  } catch (e) {
    console.error(e);
    setShifts([]);
  } finally {
    setIsLoadingShifts(false);
  }
};

  const fetchAttendanceData = async () => {
  if (!filters.client) {
    setErrorPopup("Please select a client first");
    return;
  }

  await Promise.all([
    fetchAttendanceSheets(), // ✅ NEW API
    fetchShiftsByFilter(),   // ⬅️ existing shift API logic
  ]);
};
  const saveOtThreshold = async () => {
    if (!filters.client) {
      setErrorPopup("Please select a client first");
      return;
    }

    if (!otValue || isNaN(parseInt(otValue))) {
      setErrorPopup("Please enter a valid OT threshold number");
      return;
    }

    try {
      const token = localStorage.getItem("Authorization") || userDetails?.token || "";
      const orgKey = parseInt(userDetails?.orgId || "6");
      
      const otData = {
        orgKey: orgKey,
        clientKey: parseInt(filters.clientKey) || null,
        roleId: parseInt(filters.jobRole) || null,
        bandId: parseInt(filters.band) || null,
        otThreshold: parseInt(otValue)
      };

      console.log("Saving OT threshold:", otData);

      const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_ENDPOINT}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(otData),
      });

      if (res.ok) {
        const responseData = await res.json();
        console.log("OT threshold saved successfully:", responseData);
        
        // Update local state
        setOtThreshold(otValue);
        setSuccessPopup("OT threshold saved successfully!");
        
        // Close modal
        setShowOtModal(false);
        setOtValue("");
      } else {
        const errorData = await res.json().catch(() => null);
        console.error("Failed to save OT threshold:", errorData);
        setErrorPopup(errorData?.message || "Failed to save OT threshold");
      }
    } catch (error) {
      console.error("Error saving OT threshold:", error);
      setErrorPopup("An error occurred while saving OT threshold");
    }
  };


  
  const deleteShift = async (id) => {
    if (!window.confirm("Are you sure you want to delete this shift?")) {
      return;
    }

    try {
      const token = localStorage.getItem("Authorization") || userDetails?.token || "";
      const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHIFT_ENDPOINT}/delete/${id}`;
      
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (res.ok) {
        setShifts((prev) => prev.filter((s) => s.id !== id));
        setSuccessPopup("Shift deleted successfully!");
      } else {
        const errorData = await res.json().catch(() => null);
        setErrorPopup(errorData?.message || "Failed to delete shift");
      }
    } catch (error) {
      console.error("Error deleting shift:", error);
      setErrorPopup("An error occurred while deleting the shift");
    }
  };
  
  const [shiftDraft, setShiftDraft] = useState({
    name: "",
    allowance: "",
    from: "",
    to: "",
    buffer: "",
  });

  const openEditShift = (shift) => {
    // Parse timing which could be "09:00 to 17:00" or similar format
    const timingParts = shift.timing ? shift.timing.split(" to ") : ["", ""];
    const from = timingParts[0]?.trim() || "";
    const to = timingParts[1]?.trim() || "";
    
    setShiftDraft({
      name: shift.name || "",
      allowance: shift.allowance?.toString() || "0",
      from: from,
      to: to,
      buffer: shift.buffer || "",
    });
    setEditingShiftId(shift.id);
    setShowShiftModal(true);
  };

  const handleSaveShift = async () => {
    // Validate client selection
    if (!filters.client) {
      setErrorPopup("Please select a client first");
      return;
    }

    try {
      const token = localStorage.getItem("Authorization") || userDetails?.token || "";
      const orgKey = parseInt(userDetails?.orgId || "6");
      console.log("Saving shift:", shiftDraft, filters);
      
      // Prepare shiftTiming and also shiftTimeFrom, shiftTimeTo as ISO strings with today's date
      const shiftTiming = `${shiftDraft.from} to ${shiftDraft.to}`;
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10); // YYYY-MM-DD
      // Ensure from/to are in HH:mm format
      const pad = (v) => v.toString().padStart(2, '0');
      const from = shiftDraft.from ? shiftDraft.from : "00:00";
      const to = shiftDraft.to ? shiftDraft.to : "00:00";
      const shiftTimeFrom = `${dateStr}T${from}:00`;
      const shiftTimeTo = `${dateStr}T${to}:00`;

      const shiftData = {
        orgKey: orgKey,
        clientKey: parseInt(filters.clientKey) || null,
        roleId: parseInt(filters.jobRole) || null,
        bandId: parseInt(filters.band) || null,
        shiftName: shiftDraft.name,
        shiftTiming: shiftTiming,
        shiftAllowance: parseInt(shiftDraft.allowance) || 0,
        bufferTime: shiftDraft.buffer,
        shiftTimeFrom,
        shiftTimeTo,
      };

      if (editingShiftId) {
        // Update existing shift - PUT API expects id in body, not in URL
        const updateData = {
          ...shiftData,
          id: editingShiftId,
        };

        const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHIFT_ENDPOINT}/update`;
        const res = await fetch(url, {
          method: 'PUT',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(updateData),
        });

        if (res.ok) {
          // Update local state with new values
          setShifts(shifts.map(s => s.id === editingShiftId ? {
            ...s,
            name: shiftDraft.name,
            timing: `${shiftDraft.from} to ${shiftDraft.to}`,
            allowance: Number(shiftDraft.allowance || 0),
            buffer: shiftDraft.buffer,
          } : s));
          setSuccessPopup("Shift updated successfully!");
        } else {
          const errorData = await res.json().catch(() => null);
          setErrorPopup(errorData?.message || "Failed to update shift");
        }
      } else {
        // Add new shift
        const url = `${config.API_BASE_URL}${config.ATTENDANCE_CONFIG_SHIFT_ENDPOINT}/add`;
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(shiftData),
        });

        if (res.ok) {
          const newShift = await res.json();
          setShifts([
            ...shifts,
            {
              id: newShift.id || Date.now(),
              name: shiftDraft.name || "New Shift",
              timing: `${shiftDraft.from} to ${shiftDraft.to}`,
              allowance: Number(shiftDraft.allowance || 0),
              buffer: shiftDraft.buffer || "",
            },
          ]);
          setSuccessPopup("Shift added successfully!");
        } else {
          const errorData = await res.json().catch(() => null);
          setErrorPopup(errorData?.message || "Failed to add shift");
        }
      }

      setShowShiftModal(false);
      setEditingShiftId(null);
      setShiftDraft({ name: "", allowance: "", from: "", to: "", buffer: "" });
    } catch (error) {
      console.error("Error saving shift:", error);
      setErrorPopup("An error occurred while saving the shift");
    }
  };

  return (
    <div className='att-config'>
      {successPopup && <SuccessPopup message={successPopup} onClose={() => setSuccessPopup(null)} />}
      {errorPopup && <ErrorPopup message={errorPopup} onClose={() => setErrorPopup(null)} />}
      {/* Filters row */}
      <div className='att-filters'>
        <Dropdown
          label="Client Name"
          value={filters.client}
          onChange={(e) => {
            const selectedValue = e.target.value;
            const selectedClient = clients.find(c => 
              (c.companyName || c.name || c.clientKey) === selectedValue
            );
            setFilters({ 
              ...filters, 
              client: selectedValue,
              clientKey: selectedClient?.clientKey || selectedClient?.id || ""
            });
          }}
          options={clients.map(c => ({
            id: c.id,
            value: c.clientName || c.name || c.clientKey,
            label: c.clientName || c.name || c.clientKey
          }))}
          keyField="id"
          valueField="value"
          labelField="label"
        />
        <Dropdown
          label="Job Role"
          value={filters.jobRole}
          onChange={(e) => setFilters({ ...filters, jobRole: e.target.value })}
          options={jobRoles.map(j => ({
            id: j.id,
            value: j.id,
            label: j.values || j.name || j.id
          }))}
          keyField="id"
          valueField="value"
          labelField="label"
        />
        <Dropdown
          label="Band"
          value={filters.band}
          onChange={(e) => setFilters({ ...filters, band: e.target.value })}
          options={bands.map(b => ({
            id: b.id,
            value: b.id,
            label: b.values || b.name || b.id
          }))}
          keyField="id"
          valueField="value"
          labelField="label"
        />
        <button type='button' className='att-go' onClick={fetchAttendanceData}>Go</button>
      </div>

      {/* Section 1: Shift List */}
      <div>
        <div className='section-header'>
          <h4 style={{ margin: 0 }}>Shift List</h4>
          <button className='add' title='Add Shift' onClick={() => setShowShiftModal(true)}>
            <GrAddCircle size={16} />
          </button>
        </div>
        <table className='att-table'>
          <thead>
            <tr>
              <th>Field names</th>
              <th>Shift Timing</th>
              <th>Shift Allowance</th>
              <th>Buffer Time</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingShifts ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                  Loading shifts...
                </td>
              </tr>
            ) : filteredShifts.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                  No shifts available
                </td>
              </tr>
            ) : (
              filteredShifts.map((s) => {
                // Prefer shiftTimeFrom/shiftTimeTo if available, else fallback to s.timing
                let timingDisplay = s.timing;
                if (s.shiftTimeFrom && s.shiftTimeTo) {
                  // Format as HH:mm to HH:mm
                  const from = new Date(s.shiftTimeFrom);
                  const to = new Date(s.shiftTimeTo);
                  const pad = (v) => v.toString().padStart(2, '0');
                  const fromStr = pad(from.getHours()) + ':' + pad(from.getMinutes());
                  const toStr = pad(to.getHours()) + ':' + pad(to.getMinutes());
                  timingDisplay = `${fromStr} to ${toStr}`;
                }
                return (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{timingDisplay}</td>
                    <td className='att-right'>{s.allowance}</td>
                    <td>{s.buffer}</td>
                    <td>
                      <div className='row-actions'>
                        <button title='Edit' onClick={() => openEditShift(s)}>
                          <CiEdit size={16} />
                        </button>
                        <button className='danger' title='Delete' onClick={() => deleteShift(s.id)}>
                          <RiDeleteBinLine size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showShiftModal && (
        <div className='att-modal-backdrop' onClick={() => setShowShiftModal(false)}>
          <div className='att-modal' onClick={(e) => e.stopPropagation()}>
            <div className='att-modal-header'>
              <h4 style={{ textAlign: 'left' }}>{editingShiftId ? 'Edit Shift Allowance' : 'Shift Allowance'}</h4>
            </div>
            <div className='att-modal-body'>
              <div className='att-modal-grid'>
                <div className='att-field'>
                  <label className='att-label'>Shift Name</label>
                  <select
                    className='att-input'
                    value={shiftDraft.name}
                    onChange={(e) => setShiftDraft({ ...shiftDraft, name: e.target.value })}
                  >
                    <option value=""></option>
                    {shiftTypes.map(st => (
                      <option key={st.id} value={st.values || st.name || st.id}>
                        {st.values || st.name || st.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='att-field'>
                  <label className='att-label'>Shift Allowance</label>
                  <input
                    className='att-input'
                    type='number'
                    value={shiftDraft.allowance}
                    onChange={(e) => setShiftDraft({ ...shiftDraft, allowance: e.target.value })}
                  />
                </div>
                <div className='att-field'>
                  <label className='att-label'>Shift Time - From</label>
                  <input
                    className='att-input'
                    type='time'
                    value={shiftDraft.from}
                    onChange={(e) => setShiftDraft({ ...shiftDraft, from: e.target.value })}
                  />
                </div>
                <div className='att-field'>
                  <label className='att-label'>Shift Time - To</label>
                  <input
                    className='att-input'
                    type='time'
                    value={shiftDraft.to}
                    onChange={(e) => setShiftDraft({ ...shiftDraft, to: e.target.value })}
                  />
                </div>
                <div className='att-field'>
                  <label className='att-label'>Buffer Time</label>
                  <input
                    className='att-input'
                    type='text'
                    value={shiftDraft.buffer}
                    onChange={(e) => setShiftDraft({ ...shiftDraft, buffer: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className='att-modal-footer'>
              <button type='button' className='csv-btn' onClick={() => {
                setShowShiftModal(false);
                setEditingShiftId(null);
                setShiftDraft({ name: "", allowance: "", from: "", to: "", buffer: "" });
              }}>Cancel</button>
              <button
                type='button'
                className='csv-btn primary'
                onClick={handleSaveShift}
              >
                {editingShiftId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>

      )}

      {/* Section: OT Threshold */}
      <div className='ot-section'>
        <div className='section-header'>
          <h4 style={{ margin: 0 }}>OT Threshold</h4>
          <button className='add' title='Add OT Threshold' onClick={() => setShowOtModal(true)}>
            <GrAddCircle size={16} />
          </button>
        </div>

        <div className='ot-field'>
          <label className='att-label'>OT Threshold*</label>
          <input
            className='ot-input'
            type='text'
            value={otThreshold}
            onChange={(e) => setOtThreshold(e.target.value)}
            placeholder=''
          />

        </div>
      </div>

      {/* Section 2: Attendance Time Sheet List */}
      <div>
        <div className='section-header'>
          <h4 style={{ margin: 0 }}>Attendance Time Sheet List</h4>
          <button className='add' title='Add Category' onClick={() => setShowTimesheetModal(true)}>
            <GrAddCircle size={16} />
          </button>
        </div>
        <table className='att-table'>
          <thead>
            <tr>
              <th>Attendance Category</th>
              <th>Pay ( Deduction )</th>
              <th>Presence Time ( Work time )</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingTimesheets ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                  Loading timesheets...
                </td>
              </tr>
            ) : timesheets.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                  No timesheets available
                </td>
              </tr>
            ) : (
              timesheets.map((t) => (
                <tr key={t.id}>
                  <td>{t.category}</td>
                  <td className='att-right'>{t.deduction}</td>
                  <td className='att-right'>{t.work}</td>
                  <td>
                    <div className='row-actions'>
                      <button title='Edit' onClick={() => openEditTimesheet(t)}>
                        <CiEdit size={16} />
                      </button>
                      <button className='danger' title='Delete' onClick={() => deleteTimesheet(t.id)}>
                        <RiDeleteBinLine size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
{showOtModal && (
  <div
    className='att-modal-backdrop'
    onClick={() => setShowOtModal(false)}
  >
    <div
      className='att-modal'
      onClick={(e) => e.stopPropagation()}
      style={{ maxWidth: 420 }}
    >
      {/* Modal Header */}
      <div className='att-modal-header'>
        <h4 style={{ textAlign: "left" }}>OT Threshold</h4>
      </div>

      {/* Modal Body */}
      <div className='att-modal-body'>
        <div className='att-field'>
          <label className='att-label'>OT Threshold</label>
          <input
            type='text'
            className='att-input'
            value={otValue}
            onChange={(e) => setOtValue(e.target.value)}
            placeholder=''
          />
        </div>
      </div>

      {/* Modal Footer */}
    <div className='att-modal-footer'>
  <button
    type='button'
    className='csv-btn'
    onClick={() => {
      setOtValue("");
      setShowOtModal(false);
    }}
  >
    Clear
  </button>

  <button
    type='button'
    className='csv-btn primary'
    onClick={saveOtThreshold}
  >
    Add
  </button>
</div>

    </div>
  </div>
)}

      {showTimesheetModal && (
        <div className='att-modal-backdrop' onClick={() => setShowTimesheetModal(false)}>
          <div className='att-modal' onClick={(e) => e.stopPropagation()}>
            <div className='att-modal-header'>
              <h4 style={{ textAlign: 'left' }}>{editingTimesheetId ? 'Edit Attendance Sheet Calculation' : 'Attendance Sheet Calculation'}</h4>
            </div>
            <div className='att-modal-body'>
              <div className='att-modal-grid'>
                <div className='att-field'>
                  <label className='att-label'>Attendance Category</label>
                  <input
                    className='att-input'
                    type='text'
                    value={timesheetDraft.category}
                    onChange={(e) => setTimesheetDraft({ ...timesheetDraft, category: e.target.value })}
                  />
                </div>
                <div className='att-field'>
                  <label className='att-label'>Pay ( Deduction % )</label>
                  <input
                    className='att-input'
                    type='text'
                    value={timesheetDraft.deduction}
                    onChange={(e) => setTimesheetDraft({ ...timesheetDraft, deduction: e.target.value })}
                  />
                </div>
                <div className='att-field'>
                  <label className='att-label'>Presence Time ( Work Time )</label>
                  <input
                    className='att-input'
                    type='text'
                    value={timesheetDraft.work}
                    onChange={(e) => setTimesheetDraft({ ...timesheetDraft, work: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className='att-modal-footer'>
              <button type='button' className='csv-btn' onClick={() => {
                setShowTimesheetModal(false);
                setEditingTimesheetId(null);
                setTimesheetDraft({ category: "", deduction: "", work: "" });
              }}>Cancel</button>
              <button
                type='button'
                className='csv-btn primary'
                onClick={handleSaveTimesheet}
              >
                {editingTimesheetId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section 3: CSV File Header Mapping */}
      <div className='csv-card'>
        <div className='csv-top'>
          <h4 style={{ margin: 0 }}>CSV File Header Mapping</h4>
          <button className='edit'><LuPencilLine />Edit</button>
        </div>

        <div className='csv-drop' style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 40 }}>
          {csvFile ? (
            <>
              <span style={{ fontSize: 15 }}>{csvFile.name}</span>
              {/* Delete icon */}
              <button
                type='button'
                onClick={handleRemoveCsvFile}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#d32f2f',
                  fontSize: 18,
                  padding: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
                title='Remove file'
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M6 6L14 14M6 14L14 6" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 15, color: '#888' }}>Attendance.csv</span>
              {/* Upload icon */}
              <button
                type='button'
                onClick={handleUploadIconClick}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#1976d2',
                  fontSize: 18,
                  padding: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
                title='Select file'
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 3V17M3 10H17" stroke="#1976d2" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
              <input
                type='file'
                accept='.csv'
                ref={fileInputRef}
                onChange={handleCsvFileChange}
                style={{ display: 'none' }}
              />
            </>
          )}
        </div>

        <div className='csv-grid'>
          <div className='csv-col'>
            <label>Client CSV File Headers*</label>
            {csvRows.map((r) => (
              <input key={`h-${r.id}`} className='csv-input' defaultValue={r.clientHeader} />
            ))}
          </div>
          <div className='csv-col'>
            <label>CSV Components</label>
            {csvRows.map((r) => (
              <input key={`c-${r.id}`} className='csv-input' defaultValue={r.component} />
            ))}
          </div>
          <div className='csv-col'>
            <label>Field Format</label>
            {csvRows.map((r) => (
              <select key={`f-${r.id}`} className='csv-select'>
                <option value=''></option>
                <option>Date</option>
                <option>String</option>
                <option>Number</option>
              </select>
            ))}
          </div>
          <div className='csv-col csv-radio-col'>
            <label style={{ visibility: "hidden" }}>Mapping Field</label>
            <div>
              {csvRows.map((r) => (
                <div key={`m-${r.id}`} style={{ height: 40, display: 'flex', alignItems: 'center' }}>
                  <input type='radio' name='mapField' defaultChecked={r.map} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className='csv-actions'>
          <button className='csv-btn' type='button'>Cancel</button>
          <button
            className='csv-btn primary'
            type='button'
            onClick={async () => {
              if (!csvFile) {
                setErrorPopup('Please select a CSV file first.');
                return;
              }
              // Get orgId and clientKey
              const orgKey = userDetails?.orgId || filters.clientKey || 6;
              const clientKey = filters.clientKey || 3;
              const formData = new FormData();
              formData.append('file', csvFile);
              // API removed as per request. No upload will be performed here.
            }}
          >
            Save CSV Mapping
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceConfig;
